import { useFragment, useQuery } from '@apollo/client';

import {
  AscProjectFragment,
  AscProjectFragmentDocument,
  GET_ASC_PROJECT,
  GetAscProjectQuery,
  GetAscProjectQueryVariables,
} from '@neo.tax/shared';

import { useAscContext } from '../context';
import { transformAscProject } from '../utils';
import { AscProject } from './useAscProjects';

export const useAscProject = (
  ascProjectId: string,
): {
  loading: boolean;
  ascProject: AscProject | null;
} => {
  const { fiscalYearId, periodName } = useAscContext();

  // First, try to get the project from the cache using fragment
  const { complete, data: cachedData } = useFragment<AscProjectFragment>({
    fragment: AscProjectFragmentDocument,
    fragmentName: 'AscProject',
    from: {
      __typename: 'AscProject',
      id: ascProjectId,
    },
    variables: { fiscalYearId, periodName },
  });

  // Determine if we should skip the query
  const shouldSkip = complete && !!cachedData;

  // Only query if the data is not complete in the cache
  const {
    data: queryData,
    error,
    loading: queryLoading,
  } = useQuery<GetAscProjectQuery, GetAscProjectQueryVariables>(GET_ASC_PROJECT, {
    variables: { ascProjectId, fiscalYearId, periodName },
    skip: shouldSkip,
    // Prevent auth errors from being thrown when project is deleted
    errorPolicy: 'all',
  });

  // Use cached data if available and complete, otherwise use query data
  const data = complete && cachedData ? cachedData : queryData?.getAscProject;

  // We're loading if the query is loading (and not skipped)
  const loading = !shouldSkip && queryLoading;

  // If there's an error and no data, return null instead of throwing
  // This handles cases where the project was deleted and we get an auth error
  if (error && !data) {
    return {
      loading: false,
      ascProject: null,
    };
  }

  // If there's an error but we have cached data, throw it so it can be caught by error boundaries
  if (error) {
    throw error;
  }

  if (!data) {
    return {
      loading,
      ascProject: null,
    };
  }

  const ascProject = transformAscProject(data);

  return {
    loading,
    ascProject,
  };
};
